// src/app/core/chatPage/chatCopmonents/chatInForm/components/AgentSettingsModal.tsx
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import { Globe, Zap, List, X, Sparkles, Check } from 'lucide-react';
import { AgentSettingsViewModel, ResponseStyle } from './AgentSettingsViewModel';

interface AgentSettingsModalProps {
    open: boolean;
    onClose: () => void;
}

export const AgentSettingsModal = observer(function AgentSettingsModal({ open, onClose }: AgentSettingsModalProps) {
    const vm = AgentSettingsViewModel.getInstance();

    const styleOptions: Array<{ value: ResponseStyle; label: string; desc: string }> = [
        { value: 'short', label: 'Краткий', desc: 'Только суть' },
        { value: 'balanced', label: 'Баланс', desc: 'Стандарт' },
        { value: 'detailed', label: 'Детальный', desc: 'С примерами' },
    ];

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="fixed inset-x-0 bottom-0 z-50 flex flex-col bg-white dark:bg-[#1C1C1E] rounded-t-[32px] shadow-2xl border-t border-white/10"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-center pt-3 pb-2">
                            <div className="w-12 h-1.5 bg-gray-300 dark:bg-white/20 rounded-full" />
                        </div>

                        <div className="px-6 pb-8 pt-2">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 bg-violet-500/10 rounded-xl">
                                        <Sparkles size={20} className="text-violet-500" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-foreground">Настройки</h2>
                                        <p className="text-xs text-muted-foreground">Управление поведением ИИ</p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 rounded-full bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <ToggleRow
                                    icon={<List size={18} className="text-violet-500" />}
                                    title="Показывать таймлайн"
                                    subtitle="Шаги и инструменты агента"
                                    value={vm.showTimeline}
                                    onChange={(v) => vm.setShowTimeline(v)}
                                />

                                <ToggleRow
                                    icon={<Globe size={18} className="text-blue-500" />}
                                    title="Веб-поиск"
                                    subtitle="Разрешить агенту искать в интернете"
                                    value={vm.webSearchEnabled}
                                    onChange={(v) => vm.setWebSearch(v)}
                                />

                                <ToggleRow
                                    icon={<Zap size={18} className="text-fuchsia-500" />}
                                    title="Deep Think"
                                    subtitle="Глубокие рассуждения перед ответом"
                                    value={vm.deepThinkEnabled}
                                    onChange={(v) => vm.setDeepThink(v)}
                                />

                                <div className="pt-2">
                                    <div className="text-sm font-semibold text-foreground mb-3">Стиль ответа</div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {styleOptions.map((opt) => {
                                            const isActive = vm.responseStyle === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => vm.setResponseStyle(opt.value)}
                                                    className={`relative flex flex-col items-center justify-center py-3 px-2 rounded-2xl text-xs font-semibold border-2 transition-all ${
                                                        isActive
                                                            ? 'bg-violet-500/10 text-violet-500 border-violet-500'
                                                            : 'bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400 border-transparent hover:bg-gray-100 dark:hover:bg-white/10'
                                                    }`}
                                                >
                                                    {isActive && (
                                                        <motion.div layoutId="styleCheck" className="absolute -top-2 -right-2 bg-violet-500 text-white rounded-full p-0.5">
                                                            <Check size={12} strokeWidth={3} />
                                                        </motion.div>
                                                    )}
                                                    <span className="text-sm">{opt.label}</span>
                                                    <span className="text-[10px] opacity-70 font-normal mt-0.5">{opt.desc}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="h-[env(safe-area-inset-bottom,20px)]" />
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
});

interface ToggleRowProps {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    value: boolean;
    onChange: (value: boolean) => void;
    disabled?: boolean;
}

function ToggleRow({ icon, title, subtitle, value, onChange, disabled }: ToggleRowProps) {
    return (
        <div className={`flex items-center gap-3 p-4 rounded-2xl bg-gray-50 dark:bg-white/5 border border-transparent transition-colors ${disabled ? 'opacity-60' : 'hover:border-gray-200 dark:hover:border-white/10'}`}>
            <div className="shrink-0 w-10 h-10 rounded-xl bg-white dark:bg-white/10 shadow-sm flex items-center justify-center">
                {icon}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground">{title}</div>
                {subtitle && <div className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</div>}
            </div>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && onChange(!value)}
                className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${
                    value ? 'bg-violet-500' : 'bg-gray-300 dark:bg-white/20'
                } ${disabled ? 'cursor-not-allowed' : ''}`}
                aria-pressed={value}
            >
                <motion.span
                    layout
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm ${
                        value ? 'right-1' : 'left-1'
                    }`}
                />
            </button>
        </div>
    );
}